import { useEffect, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

export type ProspectingTarget =
  | { contactIds: number[] }
  | { importBatchId: number }
  | { tagId: number };

/**
 * "Adicionar à prospecção": cria uma oportunidade "Na fila" por contato.
 * Quem enxerga a fila de todos escolhe o(s) SDR(s); os outros enfileiram para si.
 */
export function AddToProspectingDialog({
  open,
  onClose,
  target,
  count,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  target: ProspectingTarget | null;
  count?: number;
  /** Só depois de enfileirar de verdade — cancelar não dispara. */
  onDone?: () => void;
}) {
  const { can } = usePermissions();
  const canViewAll = can("crm:deals_view_all");
  const pipelinesQuery = trpc.prospecting.pipelines.list.useQuery(undefined, { enabled: open });
  const usersQuery = trpc.prospecting.users.list.useQuery(undefined, { enabled: open && canViewAll });
  const pipelines = pipelinesQuery.data ?? [];

  const [pipelineId, setPipelineId] = useState<string>("");
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  useEffect(() => {
    if (open && !pipelineId && pipelines.length > 0) setPipelineId(String(pipelines[0].pipeline.id));
  }, [open, pipelines, pipelineId]);

  const mut = trpc.prospecting.deals.bulkCreateFromContacts.useMutation({
    onSuccess: (r) => {
      const parts = [`${r.created} lead${r.created === 1 ? "" : "s"} na fila`];
      if (r.skippedExisting) parts.push(`${r.skippedExisting} já estavam`);
      if (r.skippedNoPhone) parts.push(`${r.skippedNoPhone} sem telefone`);
      if (r.truncated) parts.push("lista cortada em 5.000 — repita para o restante");
      toast.success(parts.join(" · "));
      onDone?.();
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const users = usersQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" /> Adicionar à prospecção</DialogTitle>
          <DialogDescription>
            {count != null ? `${count} contato${count === 1 ? "" : "s"} entra${count === 1 ? "" : "m"} na fila` : "Os contatos entram na fila"} como
            "Na fila". Quem já está em prospecção ou não tem telefone é pulado.
          </DialogDescription>
        </DialogHeader>

        {pipelines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ainda não existe fila de prospecção.{" "}
            {can("crm:prospecting_manage") ? (
              <Link href="/settings?tab=crm" className="underline">Configurar agora</Link>
            ) : (
              "Peça ao administrador para configurar em Configurações → CRM."
            )}
          </p>
        ) : (
          <div className="space-y-3">
            {pipelines.length > 1 && (
              <div className="space-y-1">
                <Label className="text-xs">Fila</Label>
                <Select value={pipelineId} onValueChange={setPipelineId}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pipelines.map((p) => (
                      <SelectItem key={p.pipeline.id} value={String(p.pipeline.id)}>{p.pipeline.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {canViewAll && users.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">SDR responsável (mais de um = rodízio)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {users.map((u) => {
                    const on = ownerIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setOwnerIds((prev) => (on ? prev.filter((x) => x !== u.id) : [...prev, u.id]))}
                        className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                      >
                        {u.name ?? u.email}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">Nenhum selecionado = ficam com você.</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>Cancelar</Button>
          <Button
            disabled={!target || !pipelineId || mut.isPending || pipelines.length === 0}
            onClick={() => target && mut.mutate({ pipelineId: Number(pipelineId), ownerUserIds: ownerIds, ...target })}
          >
            <Send className="h-4 w-4 mr-1.5" /> {mut.isPending ? "Adicionando..." : "Adicionar à fila"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
